import { hasPageBreak } from '@efdigital/ll-addon-content/utils/quiz-css-class';
import type { Quiz, QuizQuestion } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import { clone } from '@efdigital/ll-admin-v2/utils/array.ts';
import { fn } from '@ember/helper';
import { concat } from '@ember/helper';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Type } from '@warp-drive/core/types/symbols';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';

import { DragToSort } from '../../drag-to-sort.gts';
import { type EditRecordSignature } from '../../edit-record-base.gts';
import { EditRecordCollapse } from '../../edit-record-collapse.gts';
import QuestionEditorButtons from './input-question-editor-buttons.gts';
import QuestionEditorCards from './question-editor-cards.gts';
import type { QuestionManager } from './types/question-manager.ts';
import {
  addOne,
  type EditingMode,
  eq,
  getPageNumber,
  hasAnyPageBreaks,
  lookupQuestionManager,
  QUESTION_MANAGERS,
} from './utils.ts';

export class EditQuizQuestionsInput extends Component<
  EditRecordSignature<
    Quiz,
    {
      onQuestionAdded: (newQuestion: QuizQuestion) => void;
      isolate: (index: number) => void;
    }
  >
> {
  @service declare store: Store;

  @tracked __selectedIndex = -1;
  @tracked editingMode: EditingMode = 'inactive';

  readonly uniqueId = uniqueId();

  set selectedIndex(index: number) {
    this.__selectedIndex = index;
    this.args.isolate(index);
  }

  get selectedIndex(): number {
    return this.__selectedIndex;
  }

  onEdit = () => {
    this.selectedIndex = -1;
    this.editingMode = 'inactive';
  };

  removeQuestionAt = (questions: QuizQuestion[], index: number) => {
    const copy = [...questions];
    copy.splice(index, 1);
    questions.splice(0, questions.length, ...copy.map(clone));

    this.selectedIndex = -1;
    this.editingMode = 'inactive';

    const scrollToIndex = index === 0 ? 0 : index - 1;
    if (scrollToIndex < questions.length) {
      this.scrollToQuestion(questions[scrollToIndex]!);
    }
  };

  newQuestion = (manager: QuestionManager) => {
    const question: QuizQuestion = {
      [Type]: DataTypeQuiz.QuizQuestion,
      type: manager.type,
      qid: generateRandomSlug(),
      title: `<Enter ${manager.label}>`,
      markdown: '',
      css: '',
      crm: '',
      required: false,
      preview: false,
      ...structuredClone(manager.defaultData),
    };
    this.args.item.questions = [...(this.args.item.questions || []), question];
    this.selectedIndex = this.args.item.questions.length - 1;
    this.editingMode = 'inactive';

    // TODO: Remove this once we have a better way to scroll to the question
    // replacing `next` with `runTask` from ember-lifeline
    /* eslint-disable-next-line ember/no-runloop */
    next(() => {
      const element = document.getElementById(
        `${this.uniqueId}-question-title-${question.qid}`
      ) as HTMLInputElement;
      if (element) {
        element.focus();
        element.select();
      }
    });

    this.args.onQuestionAdded(question);
    this.scrollToQuestion(question);
  };

  updateQuestions = (questions: QuizQuestion[]) => {
    this.args.item.questions.splice(
      0,
      questions.length,
      ...questions.map(clone)
    );
    this.selectedIndex = -1;
    this.editingMode = 'inactive';
  };

  setQuestionTitle = (question: QuizQuestion, event: Event) => {
    const target = event.target as HTMLInputElement;
    question.title = target.value;
  };

  setEditingMode = (
    question: QuizQuestion | null,
    index: number,
    mode: EditingMode
  ) => {
    if (question) {
      const manager = lookupQuestionManager(question);
      const defaultQuestionData = manager.defaultData;

      const hasMissingData = Object.keys(defaultQuestionData).some((k) => {
        const key = k as keyof QuizQuestion;
        return (
          !(key in question) ||
          typeof question[key] === 'undefined' ||
          question[key] === null
        );
      });

      if (hasMissingData) {
        Object.assign(question, structuredClone(defaultQuestionData));
      }

      this.scrollToQuestion(question);
    }
    this.selectedIndex = index;
    this.editingMode = mode;
  };

  setFocus = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.focus();
    }
  };

  isEditing = (index: number) => {
    return this.selectedIndex === index;
  };

  isEditingMode = (index: number, mode: EditingMode) => {
    return this.selectedIndex === index && this.editingMode === mode;
  };

  shouldShowPageIndicator = (
    question: QuizQuestion,
    index: number,
    allQuestions: QuizQuestion[]
  ): boolean => {
    const questionHasPageBreak = hasPageBreak(question.css || '');
    const isFirstQuestion = index === 0;

    // Show indicator if:
    // 1. Question has page-break AND it's not the first question
    // 2. OR it's the first question AND there are any page breaks in the quiz
    if (questionHasPageBreak) {
      return !isFirstQuestion;
    } else {
      return isFirstQuestion && hasAnyPageBreaks(allQuestions);
    }
  };

  scrollToQuestion = (question: QuizQuestion) => {
    // TODO: Remove this once we have a better way to scroll to the question
    // replacing `next` with `runTask` from ember-lifeline
    /* eslint-disable-next-line ember/no-runloop */
    next(() => {
      const questionElement = document.getElementById(
        `${this.uniqueId}-question-${question.qid}`
      );
      if (questionElement) {
        questionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });
  };

  <template>
    <EditRecordCollapse
      @editLabel="Edit Questions"
      @item={{@item}}
      @context={{@context}}
      @onEdit={{this.onEdit}}
      @maxDepth={{2}}
      @collapseLabel="Questions"
      ...attributes
    >
      <:help>
        <p class="form-text">
          Define the questions that the user will be asked.
        </p>
      </:help>
      <:form as |uniqueId isDisabled isReadOnly|>
        {{#if isReadOnly}}
          {{#if @item.questions.length}}
            <div class="list-group mb-3">
              {{#each @item.questions as |question index|}}
                {{#let (lookupQuestionManager question) as |questionManager|}}
                  {{#if
                    (this.shouldShowPageIndicator
                      question index @item.questions
                    )
                  }}
                    <div
                      class="d-flex align-items-center gap-2 px-2 pt-2 bg-primary-subtle text-primary rounded mb-1"
                    >
                      <iconify-icon inline icon="ph:file-dotted" class="fs-5" />
                      <span class="fw-semibold">
                        Page
                        {{getPageNumber @item.questions index}}:
                        {{#if question.title}}
                          {{question.title}}
                        {{else}}
                          <em class="text-muted">(Untitled)</em>
                        {{/if}}
                      </span>
                    </div>
                  {{/if}}

                  {{#if questionManager}}
                    {{#let (eq this.selectedIndex index) as |isSelected|}}
                      <button
                        type="button"
                        class="list-group-item text-start fw-normal
                          {{if
                            (eq this.selectedIndex index)
                            'text-bg-dark'
                            ''
                          }}"
                        {{on
                          "click"
                          (fn
                            this.setEditingMode
                            question
                            (if isSelected -1 index)
                            "inactive"
                          )
                        }}
                      >
                        <iconify-icon inline icon={{questionManager.icon}} />
                        {{if
                          question.title
                          question.title
                          (concat "(Untitled) " questionManager.label)
                        }}{{#if question.required}}<span
                            class="text-danger fw-bold ms-1"
                          >*</span>{{/if}}
                      </button>
                    {{/let}}
                  {{else}}
                    <div class="list-group-item">
                      <iconify-icon inline icon="ph:question-bold" />
                      (Unknown question type)
                    </div>
                  {{/if}}
                {{/let}}
              {{/each}}
            </div>
          {{else}}
            <div class="alert alert-info">
              No questions have been created yet.
            </div>
          {{/if}}
        {{else}}
          <fieldset disabled={{isDisabled}} class="mb-0">
            {{#if @item.questions.length}}
              <div class="list-group mb-3 gap-1">
                <DragToSort
                  @items={{@item.questions}}
                  @updateItems={{this.updateQuestions}}
                >
                  <:item as |dropZone draggable question index|>
                    {{#let (lookupQuestionManager question) as |manager|}}
                      {{#let (eq this.selectedIndex index) as |isEditing|}}
                        {{#if
                          (this.shouldShowPageIndicator
                            question index @item.questions
                          )
                        }}
                          <div
                            class="d-flex align-items-center gap-2 px-2 pt-2 bg-primary-subtle text-primary rounded mb-1"
                          >
                            <iconify-icon
                              inline
                              icon="ph:file-dotted"
                              class="fs-5"
                            />
                            <span class="fw-semibold">
                              Page
                              {{getPageNumber @item.questions index}}:
                              {{#if question.title}}
                                {{question.title}}
                              {{else}}
                                <em class="text-muted">(Untitled)</em>
                              {{/if}}
                            </span>
                          </div>
                        {{/if}}

                        <div
                          class="question-item-container
                            {{if
                              isEditing
                              'is-editing shadow rounded-bottom-1'
                            }}"
                          id="{{this.uniqueId}}-question-{{question.qid}}"
                          tabindex="0"
                        >
                          <div
                            class="d-flex flex-row align-items-stretch position-relative"
                            {{dropZone index}}
                          >
                            <button
                              class="input-group-text justify-content-center drag-handle w-3rem rounded-end-0 border-end-0 d-flex flex-column align-items-center
                                {{if
                                  isEditing
                                  'rounded-bottom-0 text-bg-dark'
                                }}"
                              type="button"
                              {{on
                                "click"
                                (fn
                                  this.setEditingMode
                                  question
                                  index
                                  (if isEditing this.editingMode "inactive")
                                )
                              }}
                              {{on
                                "click"
                                (fn
                                  this.setFocus
                                  (concat
                                    this.uniqueId
                                    "-question-title-"
                                    question.qid
                                  )
                                )
                              }}
                              {{draggable question index}}
                              title="Drag to reorder"
                            >
                              <iconify-icon
                                inline
                                icon={{manager.icon}}
                                class="my-1 fs-4"
                              />
                              <div class="text-center mb-auto">
                                {{addOne index}}{{#if question.required}}<span
                                    class="text-danger fw-bold"
                                  >*</span>{{/if}}
                              </div>
                            </button>

                            <div class="d-flex flex-column flex-grow-1">
                              <input
                                id="{{this.uniqueId}}-question-title-{{question.qid}}"
                                type="text"
                                class="form-control list-group-item rounded-start-0 rounded-bottom-0 rounded-top-1"
                                value={{question.title}}
                                required
                                {{debounce (fn this.setQuestionTitle question)}}
                                {{on
                                  "focus"
                                  (fn
                                    this.setEditingMode
                                    question
                                    index
                                    (if isEditing this.editingMode "inactive")
                                  )
                                }}
                              />

                              <QuestionEditorButtons
                                @question={{question}}
                                @index={{index}}
                                @isEditing={{isEditing}}
                                @manager={{manager}}
                                @isEditingMode={{this.isEditingMode}}
                                @setEditingMode={{this.setEditingMode}}
                                @onRemove={{fn
                                  this.removeQuestionAt
                                  @item.questions
                                  index
                                }}
                              />
                            </div>
                          </div>

                          <QuestionEditorCards
                            @question={{question}}
                            @index={{index}}
                            @manager={{manager}}
                            @quiz={{@item}}
                            @isEditingMode={{this.isEditingMode}}
                          />
                        </div>
                      {{/let}}
                    {{/let}}
                  </:item>
                </DragToSort>
              </div>
            {{else}}
              <div class="alert alert-info">
                No questions have been created yet.
              </div>
            {{/if}}
          </fieldset>
        {{/if}}
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <BsDropdown as |dd|>
            <dd.button @size="sm">
              Add Question
              <span class="caret"></span>
            </dd.button>
            <dd.menu as |menu|>
              {{#each QUESTION_MANAGERS as |manager|}}
                {{#if manager}}
                  <menu.item>
                    <button
                      class="dropdown-item"
                      type="button"
                      {{on "click" (fn this.newQuestion manager)}}
                    >
                      <iconify-icon inline icon={{manager.icon}} class="me-2" />
                      {{manager.label}}
                    </button>
                  </menu.item>
                {{else}}
                  <menu.divider />
                {{/if}}
              {{/each}}
            </dd.menu>
          </BsDropdown>
        {{/unless}}
      </:buttons>
    </EditRecordCollapse>
  </template>
}
